import { useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import styled from 'styled-components';

import ArrowDownSVG from 'assets/icons/arrow-down-icon.svg';
import EwalletIcon from 'assets/icons/ewallet-icon.svg';
import { getNumberUnmaskStr } from 'components/FormEditReceiver/func';
import { datePicker, formatter } from 'helpers';
import useGetConfig from 'hooks/useGetConfig';
import useLang from 'hooks/useLang';

const CustomEwalletsAccordionMobile = ({ ewallets, countryConfig }) => {
  const { t } = useLang();

  const [currentEwallet, setCurrentEwallet] = useState(-1);

  const config = useGetConfig({
    countryConfig,
    item: {
      id: currentEwallet?.id,
      prop: 'ewalletId',
    },
    deps: [currentEwallet],
  });
  const { ewalletNumber, ewalletExpire } = config;

  const numberFormat = formatter.formatParse(ewalletNumber?.format);

  const handleAccordionItemOnClick = (id) => {
    if (currentEwallet === id) {
      setCurrentEwallet(-1);

      return;
    }

    setCurrentEwallet(id);
  };

  return (
    <FieldWrap>
      <FieldIcon>
        <img src={EwalletIcon} width={24} height={24} alt="" />
        {`${t('label_ewallet')} (${ewallets.length})`}
      </FieldIcon>
      {ewallets?.length ? (
        <AccordionStyled>
          {ewallets.map((ewallet, index) => (
            <AccordionItem
              key={index}
              eventKey={index}
              onClick={() => handleAccordionItemOnClick(ewallet?.id)}
            >
              <AccordionHeader>
                <AccordionHeaderText>
                  {ewallet?.ewalletProviderName}
                </AccordionHeaderText>
                <AccordionHeaderBody>
                  {currentEwallet === ewallet?.id && (
                    <>
                      <Divider />
                      <FieldBodyContentIcon>
                        <FieldBodyContent>
                          {ewalletNumber?.visible && (
                            <FieldBodyWrap>
                              <p>
                                {t('label_ewallet_id')}:{' '}
                                {ewallet?.ewalletNumber
                                  ? formatter.mask(
                                      getNumberUnmaskStr(
                                        ewallet?.ewalletNumber,
                                        numberFormat
                                      ),
                                      numberFormat
                                    )
                                  : ''}
                              </p>
                            </FieldBodyWrap>
                          )}
                          {ewalletExpire?.visible && (
                            <FieldBodyWrap>
                              <p>
                                {t('label_expiry_date')}:{' '}
                                {ewallet?.expiryDate &&
                                  datePicker.dateUTCToDateStr(
                                    datePicker.dateArrToDateUTC(
                                      ewallet?.expiryDate
                                    )
                                  )}
                              </p>
                            </FieldBodyWrap>
                          )}
                        </FieldBodyContent>
                      </FieldBodyContentIcon>
                    </>
                  )}
                </AccordionHeaderBody>
              </AccordionHeader>
            </AccordionItem>
          ))}
        </AccordionStyled>
      ) : (
        <NoEwallet>{t('label_no_ewallet')}</NoEwallet>
      )}
    </FieldWrap>
  );
};

const FieldWrap = styled.div`
  display: grid;
  width: 100%;
  height: fit-content;
  margin-top: 16px;
`;
const FieldIcon = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  color: #0e1012;

  display: flex;
  justify-content: start;
  align-items: center;
  height: fit-content;
  width: fit-content;

  & img {
    margin-right: 8px;
  }
`;

const AccordionStyled = styled(Accordion)`
  width: 100%;
  margin-top: 8px;
`;
const AccordionItem = styled(Accordion.Item)`
  border: none;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0px;
  }
`;
const AccordionHeader = styled(Accordion.Header)`
  height: fit-content;
  padding: 11px 16px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;

  & button {
    padding: 0;
    background-color: var(--ds-c-white) !important;
    box-shadow: none !important;
    display: block;

    &:focus,
    &:hover {
      z-index: 1;
    }

    &::after,
    &:not(.collapsed)::after {
      background-image: url(${ArrowDownSVG});
      background-position: center;
      background-size: 12px 8px;

      position: absolute;
      top: 10px;
      right: 0px;
    }
  }
`;
const AccordionHeaderText = styled.p`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  color: var(--ds-c-grey-dark);

  padding: 0;
  padding-right: 20px;
  margin: 0;
  height: 40px;
  display: flex;
  align-items: center;
`;
const AccordionHeaderBody = styled.div``;
const FieldBodyContentIcon = styled.div`
  display: flex;
  align-items: center;
`;
const FieldBodyContent = styled.div`
  width: 86%;
`;
const FieldBodyWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  width: 100%;
  height: fit-content;
  margin-top: 8px;

  &:first-child {
    margin-top: 0px;
  }

  & p {
    font-family: var(--ff-primary);
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: var(--ds-c-grey-dark);

    cursor: text;
    margin: 0;
    padding: 0;
  }
`;

const NoEwallet = styled.div`
  font-family: var(--ff-primary);
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: #b0b6bd;

  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 72px;
  background: #ffffff;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  margin-top: 8px;
`;

const Divider = styled.div`
  border-bottom: 1px solid #eef2f5;
  margin: 8px 0;
`;

export default CustomEwalletsAccordionMobile;
